import { useEffect, useState } from 'react'

import { Clock01Icon, UserGroupIcon } from '@hugeicons/react'
import clsx from 'clsx'
import _ from 'lodash'
import { DateTime } from 'luxon'
import Countdown from 'react-countdown'

import { getStatus } from '@/shared/config/missions'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { CardHint } from '@/shared/ui/CardHint'
import { FeaturedListCardStrip, featuredListCardClassName } from '@/shared/ui/FeaturedListCardStrip'
import { RewardBadge } from '@/shared/ui/RewardBadge'

import { MISSION_STATUS_LABELS } from '../constants/status'
import { formatMissionCountdownText } from '../lib/missionCountdownText'
import { getMissionParticipantsDisplay } from '../lib/missionParticipantsDisplay'

import type { MissionListItem } from '../hooks/useMissionsListView'

type MissionListCardProps = {
  mission: MissionListItem
  featured?: boolean
}

const buttonVariantForStatus = (
  uiStatus: MissionListItem['uiStatus']
): 'primary' | 'progress' | 'success-ghost' | 'secondary' | undefined => {
  if (uiStatus === 'available') return 'primary'
  if (uiStatus === 'progress') return 'progress'
  /* Reenviar é a ação principal, não destrutiva — `danger` fica no badge. */
  if (uiStatus === 'danger') return 'primary'
  if (uiStatus === 'success') return 'success-ghost'
  /* Bloqueada continua clicável (o card inteiro já navega pro detalhe) — o
     botão só torna essa affordance visível. Cinza neutro (`secondary`), não
     escondido: bloqueada não é "sem ação", é "sem participação ainda". */
  if (uiStatus === 'locked') return 'secondary'
  return undefined
}

const ctaLabelForMission = (
  mission: MissionListItem,
  featured?: boolean
): string | null => {
  const { uiStatus, _progress } = mission

  if (uiStatus === 'expired') return null
  if (uiStatus === 'locked') return 'Detalhes'
  if (mission._capacity?.status?.code === 'full') return null

  if (uiStatus === 'success') return 'Revisitar'
  if (uiStatus === 'progress') {
    if (_progress?.status === 'pending') return 'Aguardando'
    return 'Continuar'
  }
  if (uiStatus === 'danger') return 'Reenviar'
  if (uiStatus === 'available') return featured ? 'Participar' : 'Iniciar'

  return null
}

export const MissionListCard = ({
  mission,
  featured = false
}: MissionListCardProps) => {
  const {
    uid,
    name,
    start_at,
    end_at,
    point_reward,
    coin_reward,
    uiStatus,
    _capacity
  } = mission

  const { navigateTo } = useNavigation()

  const navigate = () => navigateTo(`/missoes/${uid}`)

  const timeStatus = getStatus({ start_at, end_at })
  const [currentTimeSlug, setCurrentTimeSlug] = useState(timeStatus?.slug)

  useEffect(() => {
    const interval = setInterval(() => {
      const next = getStatus({ start_at, end_at })?.slug
      if (next !== currentTimeSlug) setCurrentTimeSlug(next)
    }, 1000)
    return () => clearInterval(interval)
  }, [start_at, end_at, currentTimeSlug])

  const imageUrl = _.get(
    mission,
    'image.file.url',
    '/placeholders/mission.jpg'
  )
  const totalParticipants = getMissionParticipantsDisplay(mission)

  const ctaLabel = ctaLabelForMission(mission, featured)
  const ctaVariant = buttonVariantForStatus(uiStatus)
  const showCta = ctaLabel && ctaVariant && ctaLabel !== 'Aguardando'
  const isClosed = uiStatus === 'expired'
  const isLocked = uiStatus === 'locked'

  return (
    <Card
      flush={featured}
      onClick={navigate}
      className={clsx(
        'cursor-pointer',
        featured ? featuredListCardClassName('mission') : 'shadow-sm',
        isClosed && 'opacity-90'
      )}
    >
      {featured && (
        <FeaturedListCardStrip label="Missão em destaque" tone="mission" />
      )}

      <Card.Body>
        <div className="flex items-stretch gap-2 sm:gap-3">
          {/* Thumbnail — aspect-square padrão (MASTER §7 L8), não `self-stretch` */}
          <div className="flex aspect-square w-20 shrink-0 self-start overflow-hidden rounded-lg">
            <img
              src={imageUrl}
              alt=""
              className={clsx(
                'h-full w-full object-cover',
                (isClosed || isLocked) && 'opacity-65 grayscale'
              )}
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
            {/* Dois eixos, dois badges: status e recência. O `new` só aparece
                dentro da janela de 1h. */}
            <div className="flex flex-wrap items-center gap-1.5">
              {uiStatus !== 'available' && (
                <Badge variant={uiStatus} size="sm" showIcon={false}>
                  {MISSION_STATUS_LABELS[uiStatus]}
                </Badge>
              )}
              {mission.isNew && (
                <Badge variant="new" size="sm">
                  Nova
                </Badge>
              )}
            </div>

            <h3
              className={clsx(
                'm-0 line-clamp-2 text-sm font-bold leading-snug text-gray-900',
                isClosed && 'text-gray-500'
              )}
            >
              {name}
            </h3>

            {((point_reward ?? 0) > 0 || (coin_reward ?? 0) > 0) && (
              <RewardBadge.Group>
                {(point_reward ?? 0) > 0 && (
                  <RewardBadge type="xp" value={point_reward!} variant="inline" />
                )}
                {(coin_reward ?? 0) > 0 && (
                  <RewardBadge
                    type="coins"
                    value={coin_reward!}
                    variant="inline"
                  />
                )}
              </RewardBadge.Group>
            )}

            {totalParticipants !== null && (
              <span className="inline-flex items-center gap-1 text-2xs font-semibold text-gray-600">
                <UserGroupIcon size={12} aria-hidden />
                {totalParticipants} participante
                {totalParticipants === 1 ? '' : 's'}
              </span>
            )}

            {isLocked && start_at && (
              <span className="text-2xs font-semibold text-gray-500">
                Abre em breve
              </span>
            )}
          </div>

          {/* Rail — largura via token (MASTER §7 L8), não número solto */}
          <div className="flex min-h-20 w-[var(--rail-w-sm)] shrink-0 flex-col">
            <div className="flex flex-1 flex-col items-end justify-center gap-0.5 text-right">
              {currentTimeSlug === 'soon' && start_at && (
                <Countdown
                  date={DateTime.fromISO(start_at).toJSDate()}
                  zeroPadTime={2}
                  renderer={({ days, hours, minutes }) => (
                    <CardHint
                      icon={<Clock01Icon size={14} aria-hidden />}
                      className="font-semibold text-amber-600"
                    >
                      Inicia em {formatMissionCountdownText({ days, hours, minutes })}
                    </CardHint>
                  )}
                />
              )}

              {currentTimeSlug === 'open' && end_at && (
                <Countdown
                  date={DateTime.fromISO(end_at).toJSDate()}
                  zeroPadTime={2}
                  renderer={({ days, hours, minutes }) => (
                    <CardHint
                      icon={<Clock01Icon size={14} aria-hidden />}
                      className={clsx(
                        'font-semibold',
                        mission.isEndingSoon ? 'text-orange-600' : 'text-gray-600'
                      )}
                    >
                      {formatMissionCountdownText({ days, hours, minutes })}{' '}
                      restantes
                    </CardHint>
                  )}
                />
              )}

              {ctaLabel === 'Aguardando' && (
                <CardHint className="font-semibold text-status-progress">
                  Aguardando aprovação
                </CardHint>
              )}

              {_capacity?.status?.code === 'full' && (
                <CardHint className="font-semibold text-red-600">
                  Lotada
                </CardHint>
              )}
            </div>

            <div className="flex flex-col items-stretch">
              {showCta && (
                <Button
                  variant={ctaVariant}
                  size="sm"
                  className="w-full justify-center font-bold!"
                  onClick={e => {
                    e.stopPropagation()
                    navigate()
                  }}
                >
                  {ctaLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
